/**
 * EW-9 inventory reads and writes (fieldiq-api, 0621). Every key sits under the
 * `earthworks` prefix, so a stock move or a daily-log save (which uses stock)
 * refreshes the list, the item page and the log together.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type {
  EwAdjustmentWrite,
  EwFieldMaterial,
  EwInventoryOptions,
  EwMaterial,
  EwMaterialDetail,
  EwMaterialLocation,
  EwMaterialLocationWrite,
  EwMaterialWrite,
  EwMoveSaved,
  EwReceiptWrite,
  EwStockWarning,
  EwTransferWrite,
} from '../types/earthworks';
import { unwrapEw, unwrapEwList, useEwWriteFn } from './useEarthworks';

export interface EwMaterialFilters {
  q?: string;
  category?: string;
  lowStock?: boolean;
}

const EMPTY_OPTIONS: EwInventoryOptions = { categories: [], units: [], locationKinds: [], moveKinds: [], rateMaterials: [] };

function optionsOf(body: unknown): EwInventoryOptions {
  const o = (body as { options?: Partial<EwInventoryOptions> } | null)?.options;
  return { ...EMPTY_OPTIONS, ...(o ?? {}) };
}

export function useEwMaterials(filters?: EwMaterialFilters, enabled = true) {
  const params = {
    q: filters?.q?.trim() || undefined,
    category: filters?.category || undefined,
    low_stock: filters?.lowStock ? ('true' as const) : undefined,
  };
  return useQuery({
    queryKey: ['earthworks', 'materials', params] as const,
    queryFn: async () => {
      const body = (await api.earthworks.materials.list(params)).data as unknown;
      const list = unwrapEwList<EwMaterial>(body);
      const warnings = (body as { warnings?: unknown } | null)?.warnings;
      return { ...list, warnings: Array.isArray(warnings) ? (warnings as EwStockWarning[]) : [], options: optionsOf(body) };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwMaterial(id: string | undefined) {
  return useQuery({
    queryKey: ['earthworks', 'material', id ?? ''] as const,
    queryFn: async () => {
      const body = (await api.earthworks.materials.get(id as string)).data as unknown;
      return { ...unwrapEw<EwMaterialDetail>(body), options: optionsOf(body) };
    },
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwMaterialLocations(enabled = true) {
  return useQuery({
    queryKey: ['earthworks', 'materialLocations'] as const,
    queryFn: async () => {
      const body = (await api.earthworks.materialLocations.list()).data as unknown;
      const kinds = (body as { options?: { kinds?: unknown } } | null)?.options?.kinds;
      return { ...unwrapEwList<EwMaterialLocation>(body), kinds: Array.isArray(kinds) ? (kinds as EwInventoryOptions['locationKinds']) : [] };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The daily-log picker: snake_case, no prices, with the live locations. */
export function useEwFieldMaterials(enabled = true) {
  return useQuery({
    queryKey: ['earthworks', 'fieldMaterials'] as const,
    queryFn: async () => {
      const body = (await api.earthworks.field.materials()).data as unknown;
      const locations = (body as { locations?: unknown } | null)?.locations;
      return {
        ...unwrapEwList<EwFieldMaterial>(body),
        locations: Array.isArray(locations) ? (locations as { id: string; name: string }[]) : [],
      };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function moveSaved(body: unknown): EwMoveSaved {
  const b = body as Partial<EwMoveSaved> | null;
  return {
    data: (b?.data ?? { material: null, moves: [] }) as EwMoveSaved['data'],
    stockWarnings: Array.isArray(b?.stockWarnings) ? b!.stockWarnings! : [],
  };
}

export const useCreateEwMaterial = () =>
  useEwWriteFn(async (body: EwMaterialWrite) => unwrapEw<EwMaterial>((await api.earthworks.materials.create(body)).data));
export const useUpdateEwMaterial = () =>
  useEwWriteFn(async ({ id, body }: { id: string; body: EwMaterialWrite }) =>
    unwrapEw<EwMaterial>((await api.earthworks.materials.update(id, body)).data));
export const useDeleteEwMaterial = () => useEwWriteFn(async (id: string) => (await api.earthworks.materials.remove(id)).data);

export const useReceiveEwMaterial = () =>
  useEwWriteFn(async ({ id, body }: { id: string; body: EwReceiptWrite }) => moveSaved((await api.earthworks.materials.receive(id, body)).data));
export const useAdjustEwMaterial = () =>
  useEwWriteFn(async ({ id, body }: { id: string; body: EwAdjustmentWrite }) => moveSaved((await api.earthworks.materials.adjust(id, body)).data));
export const useTransferEwMaterial = () =>
  useEwWriteFn(async ({ id, body }: { id: string; body: EwTransferWrite }) => moveSaved((await api.earthworks.materials.transfer(id, body)).data));

export const useCreateEwMaterialLocation = () =>
  useEwWriteFn(async (body: EwMaterialLocationWrite) =>
    unwrapEw<EwMaterialLocation>((await api.earthworks.materialLocations.create(body)).data));
export const useUpdateEwMaterialLocation = () =>
  useEwWriteFn(async ({ id, body }: { id: string; body: EwMaterialLocationWrite }) =>
    unwrapEw<EwMaterialLocation>((await api.earthworks.materialLocations.update(id, body)).data));
export const useDeleteEwMaterialLocation = () =>
  useEwWriteFn(async (id: string) => (await api.earthworks.materialLocations.remove(id)).data);
